import { SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from '../constants/DateTimeConstants';

/**
 * Formats a signed total number of seconds as an "HH:mm:ss" elapsed time string.
 * Hours are not capped at 24 and are never rolled over into days, so 25 hours
 * renders as "25:00:00" rather than "01:00:00".
 */
export const formatElapsedTime = (totalSeconds: number): string => {
  const sign = totalSeconds < 0 ? '-' : '';
  const abs = Math.floor(Math.abs(totalSeconds));
  const hours = Math.floor(abs / SECONDS_PER_HOUR);
  const minutes = Math.floor((abs % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = abs % SECONDS_PER_MINUTE;
  const parts = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
  return `${sign}${parts}`;
};

/**
 * Formats a signed total number of seconds as a "m:ss" duration string, e.g. -90 -> "-1:30".
 */
export const formatDuration = (totalSeconds: number): string => {
  const sign = totalSeconds < 0 ? '-' : '';
  const abs = Math.abs(totalSeconds);
  const minutes = Math.floor(abs / 60);
  const seconds = abs % 60;
  return `${sign}${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Parses a duration string into a signed number of seconds.
 * Accepts "m:ss" / "mm:ss" where the seconds part is 0-59 (e.g. "1:30", "-2:05"),
 * a bare whole number of seconds (e.g. "90", "-45"), or an empty string (treated as 0).
 * Returns null when the input cannot be parsed.
 */
export const parseDuration = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return 0;
  }
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;

  let total: number;
  if (/^\d+$/.test(unsigned)) {
    total = parseInt(unsigned, 10);
  } else {
    const match = unsigned.match(/^(\d+):([0-5]?\d)$/);
    if (!match) {
      return null;
    }
    total = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  }
  return negative ? -total : total;
};
